import React, { createContext, useEffect, useReducer,useState } from "react";
import { useNavigate } from "react-router-dom";
import SplashScreen from "../utils/splashScreen/splashScreen";
import axios from "axios";
import qs from "qs";
import { clearAllSeverityCaches } from "../utils/computeSeverityKm";
import { clearGisMarkerCaches } from "../utils/gisMarkerCache";

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  isTimedOut: false,
  user: null,
  role: null,
  permissions: null,
};

const setHeader = () => {
  const token = JSON.parse(window.localStorage.getItem("token"));
  console.log("token from setHeader", token);
  axios.defaults.headers.common.token = token;
  axios.defaults.headers.post["Content-Type"] = "application/x-www-form-urlencoded";
};

const getApiBaseUrl = () => {
  const rawBaseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  return rawBaseUrl.endsWith("/webApi") ? rawBaseUrl : `${rawBaseUrl}/webApi`;
};

const authEndpoint = (path) => `${getApiBaseUrl()}/webAuth/${path}`;

const extractAuthResponse = (responseData) => {
  const container = responseData?.result || responseData || {};
  return {
    token: container?.token || responseData?.token || null,
    userDetails: container?.user_details || responseData?.user_details || null,
    role: container?.role || responseData?.role || container?.user_details?.role || responseData?.user_details?.role || null,
    permissions: container?.permissions || responseData?.permissions || container?.user_details?.permissions || responseData?.user_details?.permissions || [],
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INITIALISE": {
      console.log("Case initialize from auth context");
      const { isAuthenticated, user, role, permissions } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        role,
        permissions,
      };
    }

    case "LOGIN": {
      console.log("Case LOGIN from auth context");
      const { user, role, permissions } = action.payload;
      window.localStorage.setItem("role", role);
      window.localStorage.setItem("permissions", JSON.stringify(permissions));
      return {
        ...state,
        isAuthenticated: true,
        user,
        role,
        permissions,
      };
    }
    case "LOGOUT": {
      console.log("Case logout from context");
      return {
        ...state,
        isAuthenticated: false,
        isTimedOut: action.isTimedOut,
        user: null,
        role: null,
        permissions: null,
      };
    }
    case "REGISTER": {
      console.log("Case register from auth context;");
      const { user, role, permissions } = action.payload;
      window.localStorage.setItem("role", role);
      window.localStorage.setItem("permissions", JSON.stringify(permissions));
      return {
        ...state,
        isAuthenticated: true,
        user,
        role,
        permissions,
      };
    }

    default: {
      console.log("Case default");
      return { ...state };
    }
  }
};
const AuthContext = createContext({
  ...initialAuthState,
  //method: 'BAuth', TODO: I don't understand the meaning of this method in the context
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  activateProfile: () => Promise.resolve(),
  requestOTP: () => Promise.resolve(),
  validateOTP: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  requestDemo: () => Promise.resolve(),
  validateDemo: () => Promise.resolve(),
  useractivateProfile: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(
        authEndpoint("login"),
        {
          email,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const { token, userDetails, role, permissions } = extractAuthResponse(response.data);
      const resolvedUser = userDetails
        ? { ...userDetails, email: userDetails.email || email }
        : { email, role, permissions };

      if (token && resolvedUser?.role) {
        window.localStorage.setItem("user", JSON.stringify(resolvedUser));
        window.localStorage.setItem("token", JSON.stringify(token));
        window.localStorage.setItem("role", resolvedUser.role);
        window.localStorage.setItem("permissions", JSON.stringify(resolvedUser.permissions || []));

        // Fetch profile to get city and organization
        try {
          const profileRes = await axios.get(
            `${getApiBaseUrl()}/profile/get_profile`,
            { headers: { token } }
          );
          const profileData = profileRes.data?.result || profileRes.data || {};
          // Profile API returns organization as {organizationName, organizationEmail}
          const orgObj = profileData.organization || profileData.user_details?.organization;
          const orgName = typeof orgObj === 'string' ? orgObj : (orgObj?.organizationName || "");
          const city = profileData.city || profileData.user_details?.city || orgName;
          window.localStorage.setItem("city", city);
          window.localStorage.setItem("organization", orgName);
          resolvedUser.city = city;
          resolvedUser.organization = orgName;
          window.localStorage.setItem("user", JSON.stringify(resolvedUser));
        } catch (profileErr) {
          console.warn("Could not fetch profile:", profileErr);
        }

        dispatch({
          type: "LOGIN",
          payload: {
            user: resolvedUser,
            role: resolvedUser.role,
            permissions: resolvedUser.permissions || [],
          },
        });

        const homeRoute = "/home";
        navigate(homeRoute);
      } else {
        return "Invalid login response from server.";
      }
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.responseMessage || err?.response?.data?.message;
      if (serverMsg) return serverMsg;
      if (status === 401 || status === 403) return "Invalid email or password. Please try again.";
      if (status === 404) return "Account not found. Please check your email or sign up.";
      if (status === 429) return "Too many login attempts. Please try again later.";
      if (status >= 500) return "Server error. Please try again later.";
      return "Something went wrong. Please try again.";
    } finally {
      setLoading(false);
    }
  };

  const logout = async (isTimedOut = false) => {
    try {
      const token = JSON.parse(window.localStorage.getItem("token"));
      await axios.post(
        authEndpoint("logout"),
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      axios.defaults.headers.common.Authorization = null;
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("user");
      window.localStorage.removeItem("role");
      window.localStorage.removeItem("permissions");
      clearAllSeverityCaches();
      clearGisMarkerCaches();
      dispatch({ type: "LOGOUT", isTimedOut });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };
  const register = async (values) => {
    try {
      const { role, name, email, password, city, organization } = values;
      const payload = { role, name, email, password, city, organization };
      const response = await axios.post(
        authEndpoint("signup"),
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;
      if (data?.success === true || data?.status === "success") {
        return null;
      }
      return data?.error || data?.result || data?.message || "Signup failed.";
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.result || err?.response?.data?.responseMessage || err?.response?.data?.message;
      if (serverMsg) return serverMsg;
      if (status === 409) return "An account with this email already exists. Please sign in.";
      if (status === 400) return "Please check your details and try again.";
      if (status === 403) return "You are not authorized to sign up. Please contact your administrator.";
      if (status === 429) return "Too many attempts. Please try again later.";
      if (status >= 500) return "Server error. Please try again later.";
      return "Something went wrong. Please try again.";
    }
  };

  const requestOTP = async (email) => {
    try {
      const response = await axios.post(
        authEndpoint("requestOTP"),
        {
          email: email,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      return err.response;
    }
  }


  const validateOTP = async (email, otp) => {
    try {
      const response = await axios.post(
        authEndpoint("validateOTP"),
        {
          email,
          otp,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      return err.response;
    }
  };

  const resetPassword = async (password, confirmPassword, token) => {
    try {
      const response = await axios.post(
        authEndpoint("resetPassword"),
        {
          password,
          confirmPassword,
          token,
        },
        {
          headers: {
            // Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      return err.response;
    }
  };

  const requestDemo = async (email, name) => {
    try {
      const response = await axios.post(
        authEndpoint("requestDemo"),
        {
          email: email,
          name: name,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      return err.response;
    }
  }

  const validateDemo = async (email, name, otp) => {
    try {
      const response = await axios.post(
        authEndpoint("validateDemo"),
        {
          email,
          name,
          otp,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (err) {
      return err.response;
    }
  }

  const activateProfile = async (email, password, name, organization, city) => {
    try {
      const data = qs.stringify({
        email,
        password,
        name,
        organization,
        city,
      });
      const response = await axios.post(
        authEndpoint("activateProfile"),
        data
      );

      const user = response.data;
      if (user) {
        window.localStorage.setItem("user", JSON.stringify(user));
        dispatch({
          type: "REGISTER",
          payload: {
            user,
          },
        });
      } else {
        return response.data.responseMessage;
      }
    } catch (err) {
      return err;
    }
  };

  const useractivateProfile = async (email, password, name, organization, organization_id, city) => {
    try {
      const data = qs.stringify({
        email,
        password,
        name,
        organization,
        organization_id,
        city,
      });
      const response = await axios.post(
        authEndpoint("activateUserProfile"),
        data
      );

      const user = response.data;
      if (user) {
        window.localStorage.setItem("user", JSON.stringify(user));
        dispatch({
          type: "REGISTER",
          payload: {
            user,
          },
        });
      } else {
        return response.data.responseMessage;
      }
    } catch (err) {
      return err;
    }
  }

  // useEffect(() => {
  //   setHeader();
  //   const initialise = async () => {
  //     try {
  //       const token = JSON.parse(window.localStorage.getItem("token"));
  //       const role = window.localStorage.getItem("role");
  //       const permissions = JSON.parse(window.localStorage.getItem("permissions"));
  //       dispatch({
  //         type: "INITIALISE",
  //         payload: {
  //           isAuthenticated: true,
  //           user: null,
  //           role,
  //           permissions,
  //         },
  //       });
  //       if (!token) {
  //         dispatch({
  //           type: "INITIALISE",
  //           payload: {
  //             isAuthenticated: false,
  //             user: null,
  //             role: null,
  //             permissions: [],
  //           },
  //         });
  //       }
  //     } catch (err) {
  //       console.error("Error initialising auth context:", err);
  //       dispatch({
  //         type: "INITIALISE",
  //         payload: {
  //           isAuthenticated: false,
  //           user: null,
  //           role: null,
  //           permissions: [],
  //         },
  //       });
  //     }
  //   };
  //   initialise();
  // }, [state.user, state.role, state.permissions]);

  useEffect(() => {
    setHeader();
    const initialise = async () => {
      try {
        const token = JSON.parse(window.localStorage.getItem("token"));
        const user = JSON.parse(window.localStorage.getItem("user"));
        const role = window.localStorage.getItem("role");
        const permissions = JSON.parse(window.localStorage.getItem("permissions"));
        console.log("permissions from auth context", permissions);
        console.log("role from auth context", role);
        if (token) {
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: true,
              user,
              role,
              permissions,
            },
          });
        } else {
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: false,
              user: null,
              role: null,
              permissions: null,
            },
          });
        }
      } catch (err) {
        console.error("Error initialising auth context:", err);
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
            user: null,
            role: null,
            permissions: null,
          },
        });
      }
    };
    initialise();
  }, [state.role]);


  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout, register, activateProfile, requestOTP, validateOTP, resetPassword, requestDemo, validateDemo, useractivateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;






















// import React, { createContext, useEffect, useReducer } from "react";
// import SplashScreen from "../components/splashScreen/splashScreen";
// import axios from "axios";
// import qs from "qs";

// const initialAuthState = {
//   isAuthenticated: false,
//   isInitialised: false,
//   isTimedOut: false,
//   user: null,
//   role: null,
  
// };

// const setHeader = () => {
//   const token = JSON.parse(window.localStorage.getItem("token"));
//   console.log("token from setHeader", token);
//   axios.defaults.headers.common.token = token;
//   axios.defaults.headers.post["Content-Type"] =
//     "application/x-www-form-urlencoded";
// };

// const reducer = (state, action) => {
//   switch (action.type) {
//     case "INITIALISE": {
//       console.log("Case initialize from auth context");
//       const { isAuthenticated, user, role } = action.payload;

//       return {
//         ...state,
//         isAuthenticated,
//         isInitialised: true,
//         user,
//         role,
//       };
//     }

//     case "LOGIN": {
//       console.log("Case LOGIN from auth context");
//       const { user, role } = action.payload;
//       window.localStorage.setItem("role", role);
//       return {
//         ...state,
//         isAuthenticated: true,
//         user,
//         role,
//       };
//     }
//     case "LOGOUT": {
//       console.log("Case logout from context");
//       return {
//         ...state,
//         isAuthenticated: false,
//         isTimedOut: action.isTimedOut,
//         user: null,
//         role: null,
//       };
//     }
//     case "REGISTER": {
//       console.log("Case register from auth context;");
//       const { user, role } = action.payload;
//       window.localStorage.setItem("role", role);
//       return {
//         ...state,
//         isAuthenticated: true,
//         user,
//         role,
//       };
//     }

//     default: {
//       console.log("Case default");
//       return { ...state };
//     }
//   }
// };
// const AuthContext = createContext({
//   ...initialAuthState,
//   //method: 'BAuth', TODO: I don't understand the meaning of this method in the context
//   login: () => Promise.resolve(),
//   logout: () => Promise.resolve(),
//   register: () => Promise.resolve(),
//   activateProfile: () => Promise.resolve(),
//   requestOTP: () => Promise.resolve(),
//   validateOTP: () => Promise.resolve(),
//   resetPassword: () => Promise.resolve(),
//   requestDemo: () => Promise.resolve(),
//   validateDemo: () => Promise.resolve(),
//   useractivateProfile: () => Promise.resolve(),
// });

// export const AuthProvider = ({ children }) => {
//   const [state, dispatch] = useReducer(reducer, initialAuthState);
//   const login = async (email, password, role) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/login`,
//         {
//           email,
//           password,
//           role,
//         }
//       );
//       const user = response.data;
//       if (user) {
//         window.localStorage.setItem("token", JSON.stringify(user?.result.token));
//         window.localStorage.setItem("role", user?.result.user_details.role, JSON.stringify(user?.result.user_details));
//         dispatch({
//           type: "LOGIN",
//           payload: {
//             user: user.result.user_details,
//             role,
//             //userInfo,
//           },
//         });
//       } else {
//         return response;
//       }
//     } catch (err) {
//       return err;
//     }
//   };

//   const logout = async (isTimedOut = false) => {
//     try {
//       const token = JSON.parse(window.localStorage.getItem("token"));
//       await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/logout`,
//         null,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );
//       axios.defaults.headers.common.Authorization = null;
//       window.localStorage.removeItem("token");
//       window.localStorage.removeItem("role");
//       dispatch({ type: "LOGOUT", isTimedOut });
//     } catch (error) {
//       console.error("Logout failed:", error);
//     }
//   };
//   const register = async (email, password, name, unique_verification_code, role) => {
//     try {
//       const data = qs.stringify({
//         email,
//         password,
//         name,
//         unique_verification_code,
//         role,
//       });
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/signup`,
//         data
//       );

//       const user = response.data;
//       if (user) {
//         window.localStorage.setItem("user", JSON.stringify(user));
//         window.localStorage.setItem("role", role);
//         dispatch({
//           type: "REGISTER",
//           payload: {
//             user,
//             role,
//           },
//         });
//       } else {
//         return response.data.responseMessage;
//       }
//     } catch (err) {
//       return err;
//     }
//   };

//   const requestOTP = async (email) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/requestOTP`,
//         {
//           email: email,
//         },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );
//       return response;
//     } catch (err) {
//       return err.response;
//     }
//   }


//   const validateOTP = async (email, otp) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/validateOTP`,
//         {
//           email,
//           otp,
//         },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );
//       return response;
//     } catch (err) {
//       return err.response;
//     }
//   };

//   const resetPassword = async (password, confirmPassword, token) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/resetPassword`,
//         {
//           password,
//           confirmPassword,
//           token,
//         },
//         {
//           headers: {
//             // Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//         }
//       );
//       return response;
//     } catch (err) {
//       return err.response;
//     }
//   };

//   const requestDemo = async (email, name) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/requestDemo`,
//         {
//           email: email,
//           name: name,
//         },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );
//       return response;
//     } catch (err) {
//       return err.response;
//     }
//   }

//   const validateDemo = async (email, name, otp) => {
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/validateDemo`,
//         {
//           email,
//           name,
//           otp,
//         },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );
//       return response;
//     } catch (err) {
//       return err.response;
//     }
//   }

//   const activateProfile = async (email, password, name, organization, city) => {
//     try {
//       const data = qs.stringify({
//         email,
//         password,
//         name,
//         organization,
//         city,
//       });
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/activateProfile`,
//         data
//       );

//       const user = response.data;
//       if (user) {
//         window.localStorage.setItem("user", JSON.stringify(user));
//         dispatch({
//           type: "REGISTER",
//           payload: {
//             user,
//           },
//         });
//       } else {
//         return response.data.responseMessage;
//       }
//     } catch (err) {
//       return err;
//     }
//   };

//   const useractivateProfile = async (email, password, name, organization, organization_id, city) => {
//     try {
//       const data = qs.stringify({
//         email,
//         password,
//         name,
//         organization,
//         organization_id,
//         city,
//       });
//       const response = await axios.post(
//         `${process.env.REACT_APP_BASE_URL}webAuth/activateUserProfile`,
//         data
//       );

//       const user = response.data;
//       if (user) {
//         window.localStorage.setItem("user", JSON.stringify(user));
//         dispatch({
//           type: "REGISTER",
//           payload: {
//             user,
//           },
//         });
//       } else {
//         return response.data.responseMessage;
//       }
//     } catch (err) {
//       return err;
//     }
//   }

//   // useEffect(() => {
//   //   setHeader();
//   //   const initialise = async () => {
//   //     try {
//   //       const token = JSON.parse(window.localStorage.getItem("token"));
//   //       //const role = JSON.parse(window.localStorage.getItem("role"));
//   //       const role = window.localStorage.getItem("role");
//   //       dispatch({
//   //         type: "INITIALISE",
//   //         payload: {
//   //           isAuthenticated: true,
//   //           user: null,
//   //           role,
//   //         },
//   //       });
//   //       if (!token) {
//   //         dispatch({
//   //           type: "INITIALISE",
//   //           payload: {
//   //             isAuthenticated: false,
//   //             user: null,
//   //             role: null,
//   //           },
//   //         });
//   //       }
//   //     } catch (err) {
//   //       console.error("Error initialising auth context:", err);
//   //       dispatch({
//   //         type: "INITIALISE",
//   //         payload: {
//   //           isAuthenticated: false,
//   //           user: null,
//   //           //role: null,
//   //         },
//   //       });
//   //     }
//   //   };
//   //   initialise();
//   // }, [state.user, state.role]);

//   useEffect(() => {
//     setHeader();
//     const initialise = async () => {
//       try {
//         const token = JSON.parse(window.localStorage.getItem("token"));
//         const role = window.localStorage.getItem("role");

//         if (token) {
//           dispatch({
//             type: "INITIALISE",
//             payload: {
//               isAuthenticated: true,
//               user: null,
//               role,
//             },
//           });
//         } else {
//           dispatch({
//             type: "INITIALISE",
//             payload: {
//               isAuthenticated: false,
//               user: null,
//               role: null,
//             },
//           });
//         }
//       } catch (err) {
//         console.error("Error initialising auth context:", err);
//         dispatch({
//           type: "INITIALISE",
//           payload: {
//             isAuthenticated: false,
//             user: null,
//             role: null,
//           },
//         });
//       }
//     };
//     initialise();
//   }, [state.user, state.role]);


//   if (!state.isInitialised) {
//     return <SplashScreen />;
//   }

//   return (
//     <AuthContext.Provider value={{ ...state, login, logout, register, activateProfile, requestOTP, validateOTP, resetPassword, requestDemo, validateDemo, useractivateProfile }}>
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export default AuthContext;